import {
  type AiChatState,
  isRunningChatStatus,
} from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { TURN_IN_PROGRESS_STALE_MS } from "@intelligems/ig-types/schemas/ai/session";

/**
 * How long Continue first waits before re-reading a turn the server is still
 * running. Each later wait is longer (`SETTLED_TURN_POLL_BACKOFF`, capped at
 * `SETTLED_TURN_POLL_MAX_MS`): a turn still running after the first few reads
 * is a long one, and every read loads the whole transcript.
 */
export const SETTLED_TURN_POLL_MS = 3_000;
export const SETTLED_TURN_POLL_BACKOFF = 1.5;
export const SETTLED_TURN_POLL_MAX_MS = 30_000;

/**
 * The saved session once the server has settled its turn.
 *
 * The server keeps running a turn after the page that asked for it reloads, so
 * a turn saved as running usually is: resuming it would start a second run on
 * the same session, which the server refuses. Continue waits for it instead.
 * A process that died mid-turn never settles its session; past the server's
 * stale window (`TURN_IN_PROGRESS_STALE_MS`) the session takes input again,
 * and the still-running copy is returned for the caller to resume.
 */
export const waitForSettledTurn = async (
  read: () => Promise<AiChatState | null>,
  {
    now = Date.now,
    sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms)),
    isCancelled = () => false,
    onWaiting = () => undefined,
  }: {
    now?: () => number;
    sleep?: (ms: number) => Promise<void>;
    isCancelled?: () => boolean;
    onWaiting?: () => void;
  } = {}
): Promise<AiChatState | null> => {
  const deadline = now() + TURN_IN_PROGRESS_STALE_MS;
  let delay = SETTLED_TURN_POLL_MS;
  let saved = await read();
  while (
    saved &&
    isRunningChatStatus(saved.status) &&
    now() < deadline &&
    !isCancelled()
  ) {
    onWaiting();
    await sleep(delay);
    delay = Math.min(
      delay * SETTLED_TURN_POLL_BACKOFF,
      SETTLED_TURN_POLL_MAX_MS
    );
    saved = await read();
  }
  return saved;
};
